import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../components/values/Get';
import { isCustomerAllowedToSelectWorkshop } from '../helpers/business-rules/isCustomerAllowedToSelectWorkshop';
import { getWhatHasBeenDamaged, getWorkshopDetails } from './shared/vehicle';

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';

    switch (curPageId) {
        case 'godt-at-vide':
            nextPage = 'dato';
            break;
        case 'dato':
            nextPage = 'tidspunkt';
            break;
        case 'tidspunkt':
            nextPage = 'adresse';
            break;
        case 'adresse':
            nextPage = 'beskriv-skaden';
            break;
        case 'beskriv-skaden':
            nextPage = 'koeretoej-nummerplade';
            break;
        case 'koeretoej-nummerplade':
            nextPage = 'hvilket-koeretoej';
            break;
        case 'hvilket-koeretoej':
            nextPage = 'ansvar';
            break;
        case 'ansvar':
            nextPage = 'hvad-er-skadet';
            break;
        case 'hvad-er-skadet':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'personskade':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skader-andet-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'oplysninger-om-passagerer':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skadezoner':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skade-ting':
            if (getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes') {
                nextPage = 'genstandsregistrering';
            } else {
                nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            }
            break;
        case 'genstandsregistrering':
            getNextDamagePage(curPageId, formConfig, formValues);
            break;

        case 'andres-ting-bygning-dyr':
            nextPage = 'andres-ting-bygning-dyr-oplysninger';
            break;
        case 'andres-ting-bygning-dyr-oplysninger':
            nextPage = 'foerer';
            break;
        case 'foerer':
            if (getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value === 'iDroveTheVehicle') {
                nextPage = 'paavirkning';
            } else {
                nextPage = 'foerer-oplysninger';
            }
            break;
        case 'foerer-oplysninger':
            nextPage = 'paavirkning';
            break;
        case 'paavirkning':
            nextPage = 'dokumentation';
            break;
        case 'dokumentation':
            nextPage = 'politi-og-samtykke';
            break;
        case 'politi-og-samtykke':
            if (getValueOrDefault(formConfig, formValues, 'policeContacted').value === 'Y') {
                nextPage = 'samtykke';
            } else if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cvr')) {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'om-virksomheden';
            }
            break;
        case 'samtykke':
            if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cvr')) {
                nextPage = 'find-vaerksted';
            } else {
                nextPage = 'om-virksomheden';
            }
            break;
        case 'find-vaerksted':
            nextPage = 'om-virksomheden';
            break;
        case 'om-virksomheden':
            nextPage = 'om-dig';
            break;
        case 'om-dig':
            nextPage = 'resume';
            break;
        case 'resume':
            nextPage = undefined;
            break;
        default:
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

/**
 * Decides which damage page that are the next to show, depending on where in the form flow the user is, and which damage checkboxes that have been selected.
 * @param currentPageId The id of the current page
 * @param formConfig
 * @param formValues
 * @returns Next damage page to show.
 */
const getNextDamagePage = (
    currentPageId: string,
    formConfig: FormConfig,
    formValues: FormValues
): string | undefined => {
    const damageSelections = getWhatHasBeenDamaged(formConfig, formValues);

    console.log('# damageSelections', damageSelections);

    switch (currentPageId) {
        case 'hvad-er-skadet':
            if (damageSelections.vehicle.isMyVehicleDamaged) {
                const isVehicleTypeOtherSelected =
                    getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other';

                // If a customer has selected 'other' as vehicle type -> skip the skadezoner page
                if (isVehicleTypeOtherSelected) {
                    return 'skader-andet-koeretoej';
                }

                return 'skadezoner';
            }
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            if (damageSelections.things.isOthersThingsDamaged) return 'andres-ting-bygning-dyr';
            return 'foerer';
        case 'skadezoner':
        case 'skader-andet-koeretoej':
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            if (damageSelections.things.isOthersThingsDamaged) return 'andres-ting-bygning-dyr';
            return 'foerer';

        case 'personskade':
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            if (damageSelections.things.isOthersThingsDamaged) return 'andres-ting-bygning-dyr';
            return 'foerer';
        case 'oplysninger-om-passagerer':
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            if (damageSelections.things.isOthersThingsDamaged) return 'andres-ting-bygning-dyr';
            return 'foerer';
        case 'skade-ting':
        case 'genstandsregistrering':
            if (damageSelections.things.isOthersThingsDamaged) return 'andres-ting-bygning-dyr';
            return 'foerer';
    }
};
